import React from "react"
import { ResponsiveContainer, XAxis, YAxis, Tooltip, LineChart, Line, Legend } from "recharts"
import moment from "moment-timezone"

import { Loading } from "@common/EcosuiteComponent"
import { GRAPH_COLORS } from "@common/module/EcosuiteView"
import EnergyUtils from "@dashboard/energy/EnergyUtils"
import DateRangeGraph from "../DateRangeGraph"
import GraphUtils from "@common/utils/GraphUtils"
import i18n from "src/i18n"

const { t } = i18n
export default class ForecastGenerationLineGraph extends DateRangeGraph {
  getData() {
    let data = Object.keys(this.props.graphDatums).map((dateKey) => {
      let datum = this.props.graphDatums[dateKey]
      let date = moment(dateKey)
      let entry = {
        date: date.toDate(),
        time: date.toDate().getTime(),
        name: date.format("lll"),
      }

      Object.keys(datum.assetDatums).forEach((assetCode) => {
        entry[assetCode] = this.round(datum.assetDatums[assetCode].generation)
        entry[assetCode + " (Ecosuite Forecast)"] = this.round(datum.assetDatums[assetCode].forecastGeneration)
        if (this.props.predictedGenerationDatums) {
          const simpleForecastGeneration =
            this.props.predictedGenerationDatums[dateKey] && this.props.predictedGenerationDatums[dateKey].assetDatums && this.props.predictedGenerationDatums[dateKey].assetDatums[assetCode]
              ? this.round(this.props.predictedGenerationDatums[dateKey].assetDatums[assetCode].simpleForecastGeneration)
              : null
          entry[assetCode + " (Simple Forecast)"] = simpleForecastGeneration

          const irradianceForecastGeneration =
            this.props.predictedGenerationDatums[dateKey] && this.props.predictedGenerationDatums[dateKey].assetDatums && this.props.predictedGenerationDatums[dateKey].assetDatums[assetCode]
              ? this.round(this.props.predictedGenerationDatums[dateKey].assetDatums[assetCode].irradianceForecastGeneration)
              : null
          entry[assetCode + " (Irradiance Forecast)"] = irradianceForecastGeneration

          const acEnergyForecastGeneration =
            this.props.predictedGenerationDatums[dateKey] && this.props.predictedGenerationDatums[dateKey].assetDatums && this.props.predictedGenerationDatums[dateKey].assetDatums[assetCode]
              ? this.round(this.props.predictedGenerationDatums[dateKey].assetDatums[assetCode].acEnergyForecastGeneration)
              : null
          entry[assetCode + " (AC Energy Forecast)"] = acEnergyForecastGeneration
        }
      })

      return entry
    })
    return data
  }

  renderContent() {
    if (this.props.graphDatums && this.props.datums) {
      let data = this.getData()
      let aggregation = this.props.aggregation
      let graphType = aggregation === "day" ? "linear" : "monotone"

      return (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 10 }} onClick={this.zoomDateRange}>
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={this.getDomain(this.props.range, this.props.aggregation)}
              tickFormatter={(time) => {
                return GraphUtils.dateFormat(time, this.props.range, this.props.aggregation)
              }}
            />
            <YAxis />

            <Tooltip
              wrapperStyle={{ color: "#000", pointerEvents: "auto" }}
              // eslint-disable-next-line no-unused-vars
              formatter={(value, name, props) => {
                if (this.props.normalise) {
                  return value
                }
                return EnergyUtils.formatKiloWattHours(value)
              }}
              labelFormatter={(label) => {
                return GraphUtils.formatDateTooltip(label, this.props.range, this.props.aggregation)
              }}
            />
            <Legend iconType="plainline" />

            {this.props.assets.map((asset, idx) => {
              return <Line key={idx} type={graphType} dataKey={asset.code} stroke={GRAPH_COLORS[idx % GRAPH_COLORS.length]} dot={aggregation === "day"} strokeWidth={2} />
            })}

            {this.props.assets.map((asset, idx) => {
              return (
                <Line
                  key={idx + "-forecast"}
                  type={graphType}
                  dataKey={asset.code + ` (${t("energy.labels.ecosuite_forecast")})`}
                  stroke={GRAPH_COLORS[idx % GRAPH_COLORS.length]}
                  dot={aggregation === "day"}
                  strokeDasharray="10 2"
                  strokeWidth={1}
                />
              )
            })}

            {this.props.assets.map((asset, idx) => {
              return (
                <Line
                  key={idx + "-acenergy"}
                  type={graphType}
                  dataKey={asset.code + ` (${t("energy.labels.ac_energy_forecast")})`}
                  stroke={GRAPH_COLORS[idx % GRAPH_COLORS.length]}
                  dot={aggregation === "day"}
                  strokeDasharray="6 3"
                  strokeWidth={1}
                />
              )
            })}

            {this.props.assets.map((asset, idx) => {
              return (
                <Line
                  key={idx + "-irradiance"}
                  type={graphType}
                  dataKey={asset.code + ` ${t("energy.labels.irradiance_forecast")})`}
                  stroke={GRAPH_COLORS[idx % GRAPH_COLORS.length]}
                  dot={aggregation === "day"}
                  strokeDasharray="4 4"
                  strokeWidth={1}
                />
              )
            })}

            {this.props.assets.map((asset, idx) => {
              return (
                <Line
                  key={idx + "-simple"}
                  type={graphType}
                  dataKey={asset.code + ` (${t("energy.labels.simple_forecast")})`}
                  stroke={GRAPH_COLORS[idx % GRAPH_COLORS.length]}
                  dot={aggregation === "day"}
                  strokeDasharray="2 6"
                  strokeWidth={1}
                />
              )
            })}
            {this.renderCurrentTime()}
          </LineChart>
        </ResponsiveContainer>
      )
    }

    return <Loading />
  }
}
